import { SkeletonLoader } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useListScheduledQueries } from '~/react-query-hooks/alfred/useScheduledQueries';
import { APP_BASE_PATH } from '~/routes/app-paths';

import { DeletedScheduledTaskItem } from './DeletedScheduledTaskItem';
import { ScheduledTaskItem } from './ScheduledTaskItem';
import styles from './ScheduledTaskList.module.scss';

const ScheduledTaskListSkeleton = () => (
  <div className={styles.skeleton}>
    {[200, 160, 220].map(width => (
      <SkeletonLoader
        key={width}
        areas={[
          {
            styles: {
              width: `${width}px`,
              height: '16px',
              borderRadius: '8px',
              marginLeft: '10px',
              marginTop: '2px',
            },
          },
        ]}
      />
    ))}
  </div>
);

export const ScheduledTaskList = () => {
  const { t } = useTranslation(['chat']);
  const { data, isLoading } = useListScheduledQueries({ status: 'active' });
  const { data: suspendedData, isLoading: isSuspendedLoading } =
    useListScheduledQueries({ status: 'suspended' });

  if (isLoading || isSuspendedLoading) {
    return <ScheduledTaskListSkeleton />;
  }

  const tasks = data?.scheduled_queries ?? [];
  const suspendedTasks = suspendedData?.scheduled_queries ?? [];

  if (tasks.length === 0 && suspendedTasks.length === 0) {
    return (
      <div className={styles.emptyState} data-testid="scheduled-empty-state">
        <img
          src={`${APP_BASE_PATH}/static/images/empty-state/complete-research-reports.svg`}
          alt=""
          className={styles.emptyIllustration}
          width={80}
          height={80}
        />
        <div className={styles.emptyText}>
          <p className={styles.emptyTitle}>
            {t('chat:historyTab.scheduledEmptyTitle')}
          </p>
          <p className={styles.emptyDescription}>
            {t('chat:historyTab.scheduledEmptyDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="scheduled-task-list">
      {tasks.length > 0 && (
        <>
          <h4 className={styles.sectionLabel}>
            {t('chat:historyTab.scheduledTasks')}
          </h4>
          {tasks.map(task => (
            <ScheduledTaskItem key={task.id} task={task} />
          ))}
        </>
      )}
      {suspendedTasks.length > 0 && (
        <div className={styles.deletedSection}>
          <h4 className={styles.sectionLabel}>
            {t('chat:historyTab.deletedTasks')}
          </h4>
          {suspendedTasks.map(task => (
            <DeletedScheduledTaskItem key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
};
